import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Collapse, createStyles, Divider, Text, Tooltip, UnstyledButton} from '@mantine/core';
import React, {useState} from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {LocalTime} from 'ui-library/Times';

import type {SidebarItem, SidebarSection} from './useSidebarItems';

const SECTIONS: Array<{id: SidebarSection; label: string}> = [
  {id: 'inputs', label: 'Inputs'},
  {id: 'outputs', label: 'Outputs'},
];

/** The session inputs and outputs available in the right sidebar. */
export function RightSidebarListView({
  items,
  onSelect,
  onClose,
}: {
  items: Array<SidebarItem>;
  onSelect: (id: string) => void;
  onClose: () => void;
}): JSX.Element {
  const {classes} = useStyles();
  // Empty sections are omitted, so index only the rendered ones: a divider separates a section from
  // the one rendered before it, never from a skipped one.
  const visibleSections = SECTIONS.map((section) => ({
    section,
    sectionItems: items.filter((item) => item.section === section.id),
  })).filter(({sectionItems}) => sectionItems.length > 0);
  // Sections are expanded by default; collapsing one hides its rows behind its heading chevron.
  const [collapsedSections, setCollapsedSections] = useState<ReadonlySet<SidebarSection>>(new Set());
  const toggleSection = (id: SidebarSection): void => {
    setCollapsedSections((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <>
      <div className={classes.header}>
        <Text className={classes.panelTitle} size="xs" fw={600}>
          Files &amp; records
        </Text>
        <Tooltip label="Close sidebar" withArrow={true}>
          <ActionIcon
            variant="subtle"
            size="md"
            onClick={onClose}
            aria-label="Close sidebar"
            {...getTestProps({component: 'RightSidebarLayout', element: 'close-button'})}
          >
            <BenchlingIcon icon="close" />
          </ActionIcon>
        </Tooltip>
      </div>
      <div className={classes.listBody} {...getTestProps({component: 'RightSidebarLayout', element: 'list'})}>
        {visibleSections.length === 0 && (
          <Text
            size="sm"
            color="dimmed"
            className={classes.emptyState}
            {...getTestProps({component: 'RightSidebarLayout', element: 'empty-state'})}
          >
            Files and records used or created in this chat will appear here.
          </Text>
        )}
        {visibleSections.map(({section, sectionItems}, sectionIndex) => {
          const isCollapsed = collapsedSections.has(section.id);
          return (
            <div key={section.id} className={classes.section}>
              {sectionIndex > 0 && <Divider className={classes.sectionDivider} />}
              <UnstyledButton
                className={classes.sectionHeading}
                onClick={() => {
                  toggleSection(section.id);
                }}
                aria-expanded={!isCollapsed}
                {...getTestProps({
                  component: 'RightSidebarLayout',
                  element: 'section-heading',
                  key: section.id,
                })}
                data-amp-safe={false}
              >
                <Text size="sm" fw={600} className={classes.sectionHeadingLabel}>
                  {section.label}
                </Text>
                <BenchlingIcon
                  icon={isCollapsed ? 'chevron-right' : 'chevron-down'}
                  className={classes.sectionChevron}
                />
              </UnstyledButton>
              <Collapse in={!isCollapsed}>
                {sectionItems.map((item) => {
                  // Affected objects without a supported in-app route render as static rows rather
                  // than buttons.
                  const isInteractive = item.kind !== 'affected-object' || item.openable;
                  // Objects that open outside the sidebar (in their own editor) are marked with an
                  // external-link glyph next to the name; artifacts and files open inline in the
                  // detail view and carry no such glyph.
                  const opensExternally = item.kind === 'affected-object' && item.openable;
                  const rowContent = (
                    <>
                      <span className={classes.itemIcon} aria-hidden={true}>
                        <BenchlingIcon icon={item.icon} />
                      </span>
                      <div className={classes.itemText}>
                        <div className={classes.itemLabelRow}>
                          <Text size="sm" className={classes.itemLabel}>
                            {item.label}
                          </Text>
                          {opensExternally && (
                            <BenchlingIcon icon="open" className={classes.externalIcon} aria-hidden={true} />
                          )}
                        </div>
                        {(item.action != null || item.lastTouchedAt != null) && (
                          <Text size="xs" color="dimmed" className={classes.itemSubtitle}>
                            {item.action}
                            {item.action != null && item.lastTouchedAt != null && ' · '}
                            {item.lastTouchedAt != null && <LocalTime datetime={item.lastTouchedAt} />}
                          </Text>
                        )}
                      </div>
                    </>
                  );
                  if (!isInteractive) {
                    return (
                      <div
                        key={item.id}
                        className={classes.staticItem}
                        {...getTestProps({
                          component: 'RightSidebarLayout',
                          element: 'list-item',
                          key: item.id,
                        })}
                        data-amp-safe={false}
                      >
                        {rowContent}
                      </div>
                    );
                  }
                  return (
                    <UnstyledButton
                      key={item.id}
                      className={classes.listItem}
                      onClick={() => {
                        onSelect(item.id);
                      }}
                      // Keep the accessible name the item's name even though the row also shows a
                      // subtitle (action and time), so assistive tech announces the object, not the
                      // metadata alongside it.
                      aria-label={item.label}
                      {...getTestProps({component: 'RightSidebarLayout', element: 'list-item', key: item.id})}
                      data-amp-safe={false}
                    >
                      {rowContent}
                    </UnstyledButton>
                  );
                })}
              </Collapse>
            </div>
          );
        })}
      </div>
    </>
  );
}

const useStyles = createStyles((theme) => ({
  header: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
  },
  panelTitle: {
    color: theme.colors.gray[6],
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  listBody: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    padding: theme.spacing.xs,
  },
  emptyState: {
    padding: theme.spacing.sm,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xxs,
  },
  sectionDivider: {
    marginTop: theme.spacing.xs,
    marginBottom: theme.spacing.sm,
    borderColor: theme.colors.gray[2],
  },
  sectionHeading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.radius.sm,
    color: theme.colors.gray[9],
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  sectionHeadingLabel: {
    color: theme.colors.gray[9],
  },
  sectionChevron: {
    flexShrink: 0,
    color: theme.colors.gray[6],
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.sm}`,
    borderRadius: theme.radius.sm,
    color: theme.colors.gray[9],
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  staticItem: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.sm}`,
    borderRadius: theme.radius.sm,
    color: theme.colors.gray[9],
  },
  itemIcon: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.colors.blue[0],
    color: theme.colors.blue[6],
  },
  itemText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xxs,
    textAlign: 'left',
  },
  itemLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    minWidth: 0,
  },
  itemLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  externalIcon: {
    flexShrink: 0,
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.xs,
  },
  itemSubtitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));
